import { useState } from 'react';
import { ChevronDown, LogOut, Moon, Sun } from 'lucide-react';
import { fmtDateTime } from '@foodstyles/shared';
import { useAuth } from '../context/AuthContext';
import { useNow } from '../context/NowContext';
import { useTheme } from '../context/ThemeContext';
import { apiPost } from '../lib/api';
import { useClickOutside } from '../hooks/useClickOutside';
import { Avatar, RoleBadge } from '../components/primitives';

export function TopBar({ title, sub }: { title: string; sub: string }) {
  const { user } = useAuth();
  const now = useNow();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));

  const signOut = async () => {
    await apiPost('/api/auth/logout').catch(() => {});
    // Full reload: guarantees auth state, query cache and timers are all reset.
    window.location.replace('/login');
  };

  return (
    <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-7 py-3 dark:border-slate-700 dark:bg-slate-800">
      <div className="min-w-0">
        <h1 className="truncate text-lg font-bold text-slate-800 dark:text-slate-100">{title}</h1>
        <p className="hidden truncate text-xs text-slate-400 dark:text-slate-500 sm:block">{sub}</p>
      </div>
      <div className="flex items-center gap-4">
        <span className="hidden font-mono text-xs text-slate-400 dark:text-slate-500 md:block">{fmtDateTime(now)} UK</span>
        {user && (
          <div className="relative" ref={ref}>
            <button
              onClick={() => setOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={open}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <Avatar name={user.name} size={30} />
              <span className="hidden text-sm font-semibold text-slate-700 dark:text-slate-200 sm:block">{user.name}</span>
              <RoleBadge role={user.role} sm />
              <ChevronDown size={15} className="text-slate-400" />
            </button>
            {open && (
              <div className="absolute right-0 top-full z-40 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
                <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-700">
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{user.name}</div>
                  <div className="truncate text-xs text-slate-400 dark:text-slate-500">{user.email}</div>
                </div>
                <button
                  onClick={toggleTheme}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </button>
                <button
                  onClick={signOut}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  <LogOut size={15} /> Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
